import { requestAvailabilityData, requestPriceData } from "~/domains/product/redux/actions";
import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { batch } from "react-redux";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { getPackageCode, PSSizeMode, selectBestPackBasedOnStock } from "../../../../../utils/cartHelper";
import { type AddToCartResponseItem } from "../../api/AddToCart";
import { loadCartAmounts } from "~/features/addToCart/redux/cartAmounts/actions";
import { type PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { type RootState } from "~/app/store";
import { getPackageCodes } from "~/domains/product/utils/productDescriptorUtils";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { type AddToCartItem, addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function orderDetailsMounted(): ThunkResult<void> {
  return (dispatch, _getState) => {
    dispatch(loadCartAmounts());
  };
}

export function orderListMounted(): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch(loadCartAmounts());
  };
}

export function loadSelectedProductData(selectedEntries: OrderLineItemData[]): ThunkResult<void> {
  return (dispatch, _getState) => {
    batch(() => {
      selectedEntries.forEach((entry) => {
        dispatch(requestPriceData(entry.product.code, entry.priceReference));
        dispatch(requestAvailabilityData(entry.product.code));
      });
    });
  };
}

export interface AddProductsFromOrderResult {
  responseItems: AddToCartResponseItem[];
}

export function addProductsFromOrder(
  data: OrderLineItemData[],
  overrideCart?: boolean,
): ThunkResult<Promise<AddProductsFromOrderResult>> {
  return async (dispatch, getState) => {
    const dataPromises = data
      .map((entry) => [
        dispatch(requestPriceData(entry.product.code, entry.priceReference)),
        dispatch(requestAvailabilityData(entry.product.code)),
      ])
      .flat();

    await Promise.all<unknown>(dataPromises);

    const state = getState();

    const entriesToAdd = transformOrderLineItemsToAddToCartItems(data, state);
    const responseItems = await addToCart({
      items: entriesToAdd,
      page: "ORDER_DETAILS",
      source: "unknown",
      successUi: "none",
      overrideCart,
    });
    return { responseItems };
  };
}

function getQuantity(orderItem: OrderLineItemData) {
  return orderItem.confirmedQty;
}

export const transformOrderLineItemsToAddToCartItems = (data: OrderLineItemData[], state: RootState) => {
  const entriesToAdd: AddToCartItem[] = [];

  for (const orderItem of data) {
    const quantity = getQuantity(orderItem);
    const productCode = orderItem.product.code;
    const productDescriptor = selectProductDescriptor(state, {
      productCode,
      priceReference: orderItem.priceReference,
    });
    const packagingInformation = productDescriptor?.availability?.packagingInformation;
    let packageCode = getPackageCode(orderItem.packCode);
    // 1. Find if there is availability info for the package
    let packageInfo: PackagingInformationEntry | undefined = packagingInformation?.filter(
      (packageInfo) => packageInfo.packCode === packageCode,
    )[0];
    // 2. If the package does not exist, select one based on the default algorithm
    if (!packageInfo) {
      packageInfo = selectBestPackBasedOnStock(
        quantity,
        getPackageCodes(productDescriptor),
        productDescriptor,
        PSSizeMode.LARGEST_FIRST,
      );
      if (!packageInfo) {
        console.warn(`Could not select package for selected order item at lineNo: ${orderItem.lineNo}`);
        //return Promise.reject();
      } else {
        packageCode = packageInfo.packCode;
      }
    }
    const fifteenDigit = productDescriptor.base.fifteenDigit[packageCode];

    const entry: AddToCartItem = {
      productCode,
      packageSize: packageInfo?.packSize,
      packageCode,
      quantity: quantity,
      comment: orderItem.comment,
      selectedDeliveryDate: null,
      selectedDeliveryDateIsManual: false,
      primaryWarehouseCode: packageInfo?.primaryWarehouseCode ?? "",
      priceReference: orderItem.priceReference,
      contractLine: orderItem.contractLine || undefined,
      contractNumber: orderItem.contractNumber || undefined,

      restrictedPackage: isPackageRestrictionEnabled(),
      originCountry: fifteenDigit?.countryOfOrigin ?? "",
    };
    entriesToAdd.push(entry);
  }
  return entriesToAdd;
};
